// @flow
import React from 'react';
import { Query, type QueryRenderProps } from '@apollo/client/react/components';
import {
  GET_CURRENT_USER,
  FEATURE_FLAG_IS_ENABLED,
  GET_CLIENT_INBOX_STATE,
  GET_CLIENT,
  type GetCurrentUserData,
  type GetFeatureFlagVariables,
  type GetFeatureFlagResponse,
  type GetClientInboxStateData,
  type GetClientInboxStateVariables,
  type GetClientResponse,
  type GetClientVariables,
} from 'client-web-app/queries';

import SidebarButton from './SidebarButton';

// These clients see all our third-party offerings
const APPCUES_THIRD_PARTY_FLOW_ID = '-LiFCA4g68HSFWVYMR4T';
// Multi client persons cannot access the Gusto integration because Appcues cannot put the client id in the URL
// so they see an alternate flow with a contact form for the Gusto section
const APPCUES_THIRD_PARTY_MULTI_CLIENT_FLOW_ID = '-Lvr9CYQoHpKmIKPCfRH';
// These are clients that we do not want to market to so they see a contact form instead of third-party services available
const APPCUES_NON_THIRD_PARTY_FLOW_ID = '-LjwLlaXY-OiEZYbuaAb';

const PERMITTED_INBOX_STATES = ['KEEP_UP', 'CATCH_UP'];

type SidebarAddOnsContainerProps = {
  clientId: string,
};

const SidebarAddOnsContainer = ({ clientId }: SidebarAddOnsContainerProps) => {
  if (!window.Appcues) {
    return null;
  }
  return (
    <Query query={GET_CLIENT_INBOX_STATE} variables={{ clientId }}>
      {(
        getClientInboxState: QueryRenderProps<
          GetClientInboxStateData,
          GetClientInboxStateVariables,
        >,
      ) => (
        <Query query={GET_CLIENT} variables={{ clientId }}>
          {(getClient: QueryRenderProps<GetClientResponse, GetClientVariables>) => (
            <Query query={GET_CURRENT_USER}>
              {(getCurrentUser: QueryRenderProps<GetCurrentUserData, {}>) => {
                const clientInboxState = getClientInboxState?.data?.client?.inboxState;

                const clientIsKeepUpOrCatchUpInboxState =
                  PERMITTED_INBOX_STATES.includes(clientInboxState);

                if (
                  getCurrentUser.loading ||
                  getCurrentUser.error ||
                  getClient.loading ||
                  getClient.error ||
                  !clientIsKeepUpOrCatchUpInboxState
                ) {
                  return null;
                }

                const areThirdPartyAddOnsRestricted = getClient?.data?.client?.more?.isB4AClient;

                const isMultiClientPerson =
                  getCurrentUser?.data?.currentUser?.accessibleClientIds?.length > 1;

                return (
                  <SidebarButton
                    icon="plusCircle"
                    onClick={() => {
                      if (areThirdPartyAddOnsRestricted) {
                        return window.Appcues.show(APPCUES_NON_THIRD_PARTY_FLOW_ID);
                      }
                      if (isMultiClientPerson) {
                        return window.Appcues.show(APPCUES_THIRD_PARTY_MULTI_CLIENT_FLOW_ID);
                      }
                      return window.Appcues.show(APPCUES_THIRD_PARTY_FLOW_ID);
                    }}
                    text={
                      areThirdPartyAddOnsRestricted || isMultiClientPerson ? 'Add-Ons' : 'BenchTax'
                    }
                  />
                );
              }}
            </Query>
          )}
        </Query>
      )}
    </Query>
  );
};

export default SidebarAddOnsContainer;
